import { useConstructionDocumentation } from 'composables/constructionDocumentation';
import { useDict } from 'composables/fischerPlugin';
import { defineStore } from 'pinia';

import type {
  ConstructionArea,
  ConstructionRoom,
  ConstructionSite,
  FirestopDocument,
  FirestopSummary,
} from '@/generated/graphql';

export const useConstructionDocumentationStore = defineStore('construction-documentation', {
  state() {
    return {
      pageType: null as string | null,
      projectId: null as string | null,
      project: null as ConstructionSite | null,
      areaId: null as string | null,
      area: null as ConstructionArea | null,
      roomId: null as string | null,
      room: null as ConstructionRoom | null,
      firestopSummary: null as FirestopSummary[] | null,
      firestopId: null as string | null,
      firestop: null as FirestopDocument | null,
      isArchive: false as boolean,
      isLoading: false as boolean,
    };
  },
  actions: {
    async setPageType(pageType: string) {
      const { loadConstructionDocumentationData, setBrowserPageTitle } =
        useConstructionDocumentation();
      const dict = useDict();
      this.isArchive = pageType === 'archive';

      this.pageType = pageType;

      if (this.projectId) {
        await loadConstructionDocumentationData();
      }

      let browserTitle =
        this.firestop?.name || this.room?.name || this.area?.name || this.project?.name || '';

      if (pageType === 'audit-log') {
        browserTitle = dict.get('construction-Projects.Breadcrumb.Audit-Log') || 'Audit Log';
      }

      if (pageType === 'reports') {
        browserTitle = dict.get('construction-Projects.Breadcrumb.Reports') || 'Reports';
      }

      if (pageType === 'archive') {
        browserTitle = dict.get('construction-Projects.Breadcrumb.Archive') || 'Archive';
      }

      if (pageType === 'ej-documents') {
        browserTitle = dict.get('construction-Projects.Breadcrumb.EJ-Documents') || 'EJ Documents';
      }

      if (pageType === 'third-party-products') {
        browserTitle =
          dict.get('construction-Projects.Breadcrumb.Third-Party-Products') ||
          'Third Party Products';
      }

      if (pageType === 'add-documentation-point') {
        browserTitle =
          dict.get('construction-Projects.Breadcrumb.Add-Documentation-Point') ||
          'Add Documentation Point';
      }

      if (pageType === 'manage-participants') {
        browserTitle =
          dict.get('construction-Projects.Breadcrumb.Manage-Participants') || 'Manage Participants';
      }

      if (pageType === 'search') {
        browserTitle = dict.get('construction-Projects.Breadcrumb.Search') || 'Search';
      }

      setBrowserPageTitle(browserTitle);
    },
  },
  getters: {
    isOverviewPage(state) {
      return state.pageType === 'overview';
    },
    isOverviewPageArchive(state) {
      return state.pageType === 'archive';
    },
    isProjectPage(state) {
      return state.pageType === 'project';
    },
    isProjectAreaPage(state) {
      return state.pageType === 'area';
    },
    isProjectRoomPage(state) {
      return state.pageType === 'room';
    },
    isDocumentationPointPage(state) {
      return state.pageType === 'documentation-point';
    },
    isAddDocumentationPointPage(state) {
      return state.pageType === 'add-documentation-point';
    },
    isAuditLogPage(state) {
      return state.pageType === 'audit-log';
    },
    isReportsPage(state) {
      return state.pageType === 'reports';
    },
    isEJDocumentsPage(state) {
      return state.pageType === 'ej-documents';
    },
    isThirdPartyProductsPage(state) {
      return state.pageType === 'third-party-products';
    },
    isManageParticipantsPage(state) {
      return state.pageType === 'manage-participants';
    },
    isSearchPage(state) {
      return state.pageType === 'search';
    },
    isAdmin(state) {
      return state.project?.role === 'ADMIN';
    },
    isOffice(state) {
      return state.project?.role === 'OFFICE';
    },
    isAuditor(state) {
      return state.project?.role === 'AUDITOR';
    },
    isEmployee(state) {
      return state.project?.role === 'EMPLOYEE';
    },
    isOwner(state) {
      return state.project?.role === 'OWNER';
    },
    isContributor(state) {
      return state.project?.role === 'CONTRIBUTOR';
    },
    isDemo(state) {
      return state.project?.role === 'DEMO';
    },
  },
});
